import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import app, { db } from "../../firebase";
import { httpsCallable, getFunctions } from "firebase/functions";
import "./brochure_modal.css";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  arrayUnion,
  increment,
} from "firebase/firestore";
import { brochureHTML } from "./email_template";

const EmailModal = ({ isOpen, onClose, programType = "Price Action Trader" }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      setMessage("Please enter your name");
      setIsSuccess(false);

      return;
    }

    if (!email || !email.includes("@")) {
      setMessage("Please enter a valid email address");
      setIsSuccess(false);

      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      try {
        const emailDocRef = doc(db, "brochures_emails", email);
        const emailDoc = await getDoc(emailDocRef);

        if (emailDoc.exists()) {
          await setDoc(
            emailDocRef,
            {
              fullName: fullName.trim(),
              requests: arrayUnion({
                programType,
                fullName: fullName.trim(),
                timestamp: new Date().toISOString(),
                source: "brochure_download",
                status: "submitted",
              }),
              totalRequests: increment(1),
              lastRequestDate: serverTimestamp(),
            },
            { merge: true },
          );
        } else {
          await setDoc(emailDocRef, {
            email,
            fullName: fullName.trim(),
            requests: [
              {
                programType,
                fullName: fullName.trim(),
                timestamp: new Date().toISOString(),
                source: "brochure_download",
                status: "submitted",
              },
            ],
            firstRequestDate: serverTimestamp(),
            totalRequests: 1,
          });
        }
      } catch (firestoreError) {
        console.error("Error saving to Firestore:", firestoreError);
      }

      const callableFunctions = getFunctions(app, "us-central1");

      console.log("Callable projectId:", app.options.projectId);

      const sendEmail = httpsCallable(callableFunctions, "sendBrochureEmail");
      const brochureFilename = "TPC TECHNICAL TRADING BOOTCAMP.pdf";
      const htmlData = brochureHTML({
        userEmail: email,
        studentName: fullName.trim(),
        programType,
      });

      const result = await sendEmail({
        to: email,
        from: "TPC Brochure <noreply@mail.andus.co.za>",
        subject: "Your Technical Trading Brochure",
        isHTML: true,
        html: htmlData,
        emailType: "brochureDownload",
        studentName: fullName.trim(),
        attachmentStoragePath: `brochures/${brochureFilename}`,
        attachmentFilename: brochureFilename,
      });

      if (result.data.success) {
        setMessage("Brochure sent successfully! Please check your email.");
        setIsSuccess(true);
        setShowSuccessModal(true);

        setTimeout(() => {
          setShowSuccessModal(false);
          onClose();
        }, 1000);
      } else {
        setMessage("Failed to send brochure. Please try again.");
        setIsSuccess(false);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setMessage("An error occurred. Please try again later.");
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = showSuccessModal ? (
    <div className="email-modal-overlay" role="dialog" aria-modal="true">
      <div className="success-modal">
        <svg
          className="success-checkmark"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            className="success-checkmark-circle"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            className="success-checkmark-check"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
        <h2 className="success-modal-title">Success!</h2>
        <p className="success-modal-message">
          Brochure has been sent successfully
        </p>
      </div>
    </div>
  ) : (
    <div
      className="email-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
      onClick={onClose}
    >
      <div className="email-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="email-modal-close"
          onClick={onClose}
          aria-label="Close brochure modal"
        >
          ×
        </button>

        <div className="email-modal-content">
          <h2 id="email-modal-title">Download Your Brochure</h2>

          <p>
            Enter your email address to receive the Price Action Trader Bootcamp
            brochure.
          </p>

          <form onSubmit={handleSubmit} className="email-modal-form">
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your name"
              required
              disabled={isLoading}
              className="email-input"
            />

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={isLoading}
              className="email-input"
            />

            {message && (
              <div
                className={`email-message ${isSuccess ? "success" : "error"}`}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="email-submit-btn"
            >
              {isLoading ? "Sending..." : "Send Brochure"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EmailModal;
